import { CLASSES, SELECTORS } from './modules/lib';
import { isRideshareClosedForSession, rememberClosedRideshare } from './modules/session-storage';

const TRIGGER_ANIMATION_DURATION = 500;

const initRideshare = () => {
	const rideshare = document.querySelector(SELECTORS.rideshare);
	if (!rideshare) return;

	let lastScrollTop = 0;
	let hasAutoClosedContent = false;
	let userInteracted = false;
	const scrollUpIndicator = rideshare.querySelector(SELECTORS.scroll);

	// Calculate bottom offset.
	const footer = document.querySelector(SELECTORS.footer);
	const bottomOffset = footer?.getBoundingClientRect().height ?? 0;

	// Close all open rideshare item contents.
	const closeAllItemContents = () => {
		const openItems = rideshare.querySelectorAll(`.${CLASSES.itemOpened}`);
		openItems.forEach((item) => {
			item.classList.add(CLASSES.itemClosing);
			item.classList.remove(CLASSES.itemOpened);
			item.querySelector(SELECTORS.toggle)?.setAttribute('aria-expanded', 'false');
			setTimeout(() => item.classList.remove(CLASSES.itemClosing), TRIGGER_ANIMATION_DURATION);
		});

		return openItems.length > 0;
	};

	// Close rideshare when escape key is pressed.
	document.addEventListener('keydown', (event) => {
		if (event.key === 'Escape' && closeAllItemContents()) {
			rememberClosedRideshare();
		}
	});

	// Close rideshare when clicking outside of it.
	document.addEventListener('click', (event) => {
		const eventPath = typeof event.composedPath === 'function' ? event.composedPath() : [];
		const isClickInside = eventPath.includes(rideshare) || (event.target instanceof Node && rideshare.contains(event.target));
		if (!isClickInside) {
			closeAllItemContents();
		}
	});

	// Add classes to the rideshare element based on the scroll position.
	const onScroll = () => {
		const classList = rideshare.classList;
		const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
		const isScrollingDown = scrollTop > lastScrollTop;

		if (scrollTop === 0) {
			// Page is at the top.
			classList.remove(CLASSES.scrolling);
			classList.add(CLASSES.scrollTop);
		} else {
			// Page is scrolled.
			classList.remove(CLASSES.scrollTop);
			classList.add(CLASSES.scrolling);

			// Close all item contents if the user scrolls down and hasn't interacted with any item.
			if (isScrollingDown && scrollTop > 200 && !hasAutoClosedContent && !userInteracted) {
				hasAutoClosedContent = true;
				closeAllItemContents();
			}

			// Reached the bottom of the page.
			if (window.innerHeight + window.scrollY >= document.body.offsetHeight - bottomOffset) {
				classList.add(CLASSES.scrollBottom);
			} else {
				classList.remove(CLASSES.scrollBottom);
			}
		}

		lastScrollTop = scrollTop;
	};

	if (scrollUpIndicator) {
		scrollUpIndicator.addEventListener('click', () => {
			window.scrollTo({ top: 0, behavior: 'smooth' });
		});
	}

	onScroll();
	window.addEventListener('scroll', onScroll, { passive: true });

	const openItemContent = (item: HTMLElement) => {
		closeAllItemContents();
		item.classList.remove(CLASSES.itemClosing);
		item.classList.add(CLASSES.itemOpened);
		item.querySelector(SELECTORS.toggle)?.setAttribute('aria-expanded', 'true');
	};

	// Toggle individual rideshare item content.
	const toggleItemContent = (item: HTMLElement) => {
		const isOpened = item.classList.contains(CLASSES.itemOpened);

		if (isOpened) {
			closeAllItemContents();
			rememberClosedRideshare();
		} else {
			openItemContent(item);
		}
	};

	// Setup event listeners for all item triggers (excluding scroll-up trigger).
	const itemTriggers = rideshare.querySelectorAll(SELECTORS.itemTrigger);
	itemTriggers.forEach((trigger) => {
		if (trigger !== scrollUpIndicator) {
			trigger.setAttribute('aria-expanded', 'false');
			trigger.addEventListener('click', () => {
				userInteracted = true;
				const item = trigger.closest<HTMLElement>(SELECTORS.item);
				if (item) toggleItemContent(item);
			});
		}
	});

	// Open rideshare if the user is at the top of the page and has not closed the rideshare.
	setTimeout(() => {
		if (userInteracted || window.scrollY !== 0 || isRideshareClosedForSession()) return;

		const openItemAttr = rideshare.getAttribute('data-open-item');
		const itemToOpen = openItemAttr ? rideshare.querySelector<HTMLElement>(`.rideshare__item--${openItemAttr}`) : null;

		if (itemToOpen) {
			openItemContent(itemToOpen);
		}
	}, 500);
};

initRideshare();
